import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { colorForTeacher, matchesTeacher } from "@/lib/teacherColors";
import { useI18n } from "@/i18n/i18n";
import { useAuth } from "@/context/AuthContext";
import { useIsMobile } from "@/hooks/useIsMobile";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Loader2, CalendarDays, Sparkles, Repeat, Recycle } from "lucide-react";
import { toISODate, todayISO } from "@/lib/dateFormat";

const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const HOUR_START = 7;   // 07:00
const HOUR_END = 22;    // 22:00
const HOUR_PX = 56;     // each hour ~56px tall (Google Calendar feel)
const DEFAULT_DURATION = 60;

// Stable color palette indexed by group_id
const PALETTE = [
  "bg-blue-100 text-blue-900 border-blue-300",
  "bg-emerald-100 text-emerald-900 border-emerald-300",
  "bg-amber-100 text-amber-900 border-amber-300",
  "bg-purple-100 text-purple-900 border-purple-300",
  "bg-pink-100 text-pink-900 border-pink-300",
  "bg-cyan-100 text-cyan-900 border-cyan-300",
  "bg-orange-100 text-orange-900 border-orange-300",
  "bg-lime-100 text-lime-900 border-lime-300",
];
const colorForGroup = (gid) => {
  if (!gid) return PALETTE[0];
  let h = 0;
  for (const c of gid) h = (h * 31 + c.charCodeAt(0)) % PALETTE.length;
  return PALETTE[h];
};

// "Hoy" siempre es el día de Costa Rica (mediodía para que ninguna zona lo mueva).
const crToday = () => new Date(`${todayISO()}T12:00:00`);
const mondayOf = (d) => {
  const x = new Date(d);
  const day = x.getDay() || 7;
  if (day !== 1) x.setHours(-24 * (day - 1));
  x.setHours(0, 0, 0, 0);
  return x;
};

const fmtRange = (mon) => {
  const sun = new Date(mon);
  sun.setDate(sun.getDate() + 6);
  const o = { month: "short", day: "numeric" };
  return `${mon.toLocaleDateString(undefined, o)} – ${sun.toLocaleDateString(undefined, { ...o, year: "numeric" })}`;
};

const parseTime = (s) => {
  if (!s) return { h: 9, m: 0 };
  const [hh, mm] = s.split(":");
  return { h: parseInt(hh, 10) || 0, m: parseInt(mm, 10) || 0 };
};

const minutesBetween = (a, b) => {
  const aMin = a.h * 60 + a.m;
  const bMin = b.h * 60 + b.m;
  return Math.max(15, bMin - aMin);
};

export default function CalendarWeek({ onSwitchView, groupFilter = "all", groups = [], onGroupFilterChange, teacherFilter = "all", teachers = [], onTeacherFilterChange }) {
  const { t, lang } = useI18n();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const [monday, setMonday] = useState(() => mondayOf(crToday()));
  const [data, setData] = useState({ events: [] });
  const [loading, setLoading] = useState(true);

  const load = async (mon) => {
    setLoading(true);
    try {
      const iso = toISODate(mon);
      const r = await api.get("/calendar", { params: { week: iso } });
      setData(r.data);
    } catch (e) {
      // Just leave events empty if it fails
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(monday); }, [monday]);

  const days = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [monday]);

  // Group events by date, respecting the group filter passed from the parent.
  const eventsByDate = useMemo(() => {
    const byDate = {};
    for (const e of (data.events || [])) {
      if (e.type !== "class" && e.type !== "extra_session" && e.type !== "live_session") continue;
      if (e.type !== "live_session" && groupFilter !== "all" && e.group_id !== groupFilter) continue;
      if (!matchesTeacher(e, teacherFilter)) continue;
      const key = e.date;
      (byDate[key] = byDate[key] || []).push(e);
    }
    return byDate;
  }, [data, groupFilter, teacherFilter]);

  const goPrev = () => {
    const m = new Date(monday); m.setDate(m.getDate() - 7); setMonday(m);
  };
  const goNext = () => {
    const m = new Date(monday); m.setDate(m.getDate() + 7); setMonday(m);
  };
  const goToday = () => setMonday(mondayOf(crToday()));

  const totalH = HOUR_END - HOUR_START;
  const hourLabels = Array.from({ length: totalH }, (_, i) => HOUR_START + i);
  const today = crToday(); today.setHours(0, 0, 0, 0);

  return (
    <div className="space-y-4" data-testid="calendar-week-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-brand" />
          <h2 className="font-display text-2xl font-bold text-slate-900">{fmtRange(monday)}</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={goPrev} data-testid="week-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToday} data-testid="week-today">
            {t("today")}
          </Button>
          <Button variant="outline" size="sm" onClick={goNext} data-testid="week-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
          {onSwitchView && (
            <div className="ml-2 inline-flex rounded-lg overflow-hidden border border-slate-300">
              <button
                type="button"
                onClick={() => onSwitchView("month")}
                className="px-3 py-1.5 text-xs font-medium bg-white text-slate-600 hover:bg-slate-50"
                data-testid="view-month-btn"
              >
                {t("view_month")}
              </button>
              <button
                type="button"
                onClick={() => onSwitchView("week")}
                className="px-3 py-1.5 text-xs font-semibold bg-orange-500 text-white"
                data-testid="view-week-btn"
              >
                {t("view_week")}
              </button>
            </div>
          )}
          {/* Group filter — mirrors the month view so admins/teachers with
              many groups can focus on one at a time. Only shown when there
              are 2+ groups available. */}
          {onTeacherFilterChange && teachers.length > 1 && (
            <select
              value={teacherFilter}
              onChange={(e) => onTeacherFilterChange(e.target.value)}
              className="ml-2 text-sm border border-slate-200 rounded-md px-3 py-1.5 bg-white"
              data-testid="week-teacher-filter"
            >
              <option value="all">{t("all_teachers")}</option>
              {teachers.map((tc) => <option key={tc.id} value={tc.id}>{tc.name}</option>)}
            </select>
          )}
          {onGroupFilterChange && groups.length > 1 && (
            <select
              value={groupFilter}
              onChange={(e) => onGroupFilterChange(e.target.value)}
              className="ml-2 text-sm border border-slate-200 rounded-md px-3 py-1.5 bg-white"
              data-testid="week-group-filter"
            >
              <option value="all">{t("all_groups")}</option>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        {loading ? (
          <div className="p-10 flex items-center justify-center text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t("loading")}
          </div>
        ) : isMobile ? (
          /* Mobile: agenda list instead of the 7-column grid */
          <div className="divide-y divide-slate-100" data-testid="week-agenda-mobile">
            {days.map((d, i) => {
              const iso = toISODate(d);
              const dayEvents = (eventsByDate[iso] || []).slice().sort((a, b) =>
                ((a.start_time || a.schedule_start_time || "") > (b.start_time || b.schedule_start_time || "") ? 1 : -1));
              const isToday = d.toDateString() === today.toDateString();
              return (
                <div key={i} className="p-3" data-testid={`agenda-day-${iso}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs font-semibold uppercase tracking-wider ${isToday ? "text-orange-600" : "text-slate-500"}`}>
                      {t(DAY_KEYS[i] + "_short", { _fallback: DAY_KEYS[i] }) || DAY_KEYS[i]} {d.getDate()}
                    </span>
                    {isToday && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-semibold">
                        {t("today")}
                      </span>
                    )}
                  </div>
                  {dayEvents.length === 0 ? (
                    <div className="text-xs text-slate-400">—</div>
                  ) : (
                    <div className="space-y-2">
                      {dayEvents.map((ev, ei) => {
                        const isLive = ev.type === "live_session";
                        const st = isLive ? ev.start_time : ev.schedule_start_time;
                        const en = isLive ? ev.end_time : ev.schedule_end_time;
                        return (
                          <div key={ei} data-testid={`agenda-event-${iso}-${ei}`}
                            className={`rounded-lg border px-3 py-2 ${isLive ? "bg-teal-50 border-teal-200" : "bg-slate-50 border-slate-200"}`}>
                            <div className="text-[11px] font-mono text-slate-500">
                              {st}{en ? `–${en}` : ""}
                            </div>
                            <div className="text-sm font-medium text-slate-900">
                              {isLive ? ev.title : ev.group_name}
                            </div>
                            {isLive && (
                              <div className="text-[11px] text-slate-500">WSS Live{ev.teacher_name ? ` · ${ev.teacher_name}` : ""}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="min-w-[900px]">
            {/* Header row */}
            <div className="grid grid-cols-[64px_repeat(7,_minmax(0,1fr))] border-b border-slate-200 bg-slate-50">
              <div className="p-2 text-[11px] text-slate-500"></div>
              {days.map((d, i) => {
                const isToday = d.toDateString() === today.toDateString();
                return (
                  <div
                    key={i}
                    className={`p-2 text-center border-l border-slate-200 ${isToday ? "bg-orange-50" : ""}`}
                  >
                    <div className="text-[11px] uppercase tracking-wider text-slate-500">
                      {t(DAY_KEYS[i] + "_short", { _fallback: DAY_KEYS[i] }) || DAY_KEYS[i]}
                    </div>
                    <div className={`text-xl font-bold mt-0.5 ${isToday ? "text-orange-600" : "text-slate-900"}`}>
                      {d.getDate()}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Grid */}
            <div className="grid grid-cols-[64px_repeat(7,_minmax(0,1fr))] relative">
              {/* Hour column */}
              <div className="border-r border-slate-200">
                {hourLabels.map((h) => (
                  <div
                    key={h}
                    className="text-[10px] text-slate-500 px-2 pt-0.5 border-b border-slate-100"
                    style={{ height: HOUR_PX }}
                  >
                    {String(h).padStart(2, "0")}:00
                  </div>
                ))}
              </div>

              {/* Day columns */}
              {days.map((d, di) => {
                const isToday = d.toDateString() === today.toDateString();
                const iso = toISODate(d);
                const dayEvents = eventsByDate[iso] || [];

                return (
                  <div
                    key={di}
                    className={`relative border-l border-slate-200 ${isToday ? "bg-orange-50/30" : ""}`}
                    style={{ height: totalH * HOUR_PX }}


                    data-testid={`week-day-col-${iso}`}
                  >
                    {/* Hour grid lines */}
                    {hourLabels.map((_, hi) => (
                      <div
                        key={hi}
                        className="absolute left-0 right-0 border-b border-slate-100"
                        style={{ top: hi * HOUR_PX, height: HOUR_PX }}
                      />
                    ))}

                    {/* Events */}
                    {dayEvents.map((ev, ei) => {
                      const isLive = ev.type === "live_session";
                      const startStr = isLive ? (ev.start_time || "09:00") : (ev.schedule_start_time || "09:00");
                      const endStr = isLive ? (ev.end_time || null) : (ev.schedule_end_time || null);
                      const start = parseTime(startStr);
                      const end = endStr ? parseTime(endStr) : { h: start.h, m: start.m + DEFAULT_DURATION };
                      const dur = minutesBetween(start, end);
                      const topMin = (start.h - HOUR_START) * 60 + start.m;
                      if (topMin < 0 || topMin > totalH * 60) return null;
                      const top = (topMin / 60) * HOUR_PX;
                      const height = Math.max(28, (dur / 60) * HOUR_PX - 2);
                      const isExtra = ev.type === "extra_session";
                      const extraIcon =
                        ev.session_type === "reschedule" ? <Repeat className="h-3 w-3" /> :
                        ev.session_type === "makeup" ? <Recycle className="h-3 w-3" /> :
                        <Sparkles className="h-3 w-3" />;
                      const base = isLive
                        ? (ev.my_booking_status === "waitlisted"
                          ? "bg-amber-100 text-amber-900 border-amber-300"
                          : "bg-teal-100 text-teal-900 border-teal-400")
                        : isExtra
                        ? (ev.session_type === "reschedule"
                          ? "bg-orange-200 text-orange-900 border-orange-400 border-dashed"
                          : ev.session_type === "makeup"
                          ? "bg-amber-200 text-amber-900 border-amber-400 border-dashed"
                          : "bg-purple-200 text-purple-900 border-purple-400")
                        : (teachers.length ? colorForTeacher(teachers, ev.teacher_id) : colorForGroup(ev.group_id));
                      return (
                        <div
                          key={ei}
                          data-testid={isLive ? `week-live-${iso}-${ei}` : `week-event-${iso}-${ev.group_id}-${ei}`}
                          className={`absolute left-0.5 right-0.5 rounded-md border-2 px-1.5 py-1 overflow-hidden cursor-default ${base}`}
                          style={{ top, height }}
                          title={isLive ? `WSS Live · ${ev.title} · ${startStr}${endStr ? "–" + endStr : ""}` : `${ev.group_name} · ${startStr}${endStr ? "–" + endStr : ""}`}
                        >
                          <div className="text-[10px] font-mono opacity-80">
                            {startStr}{endStr ? `–${endStr}` : ""}
                          </div>
                          <div className="text-[11px] font-semibold leading-tight flex items-center gap-1">
                            {isExtra && extraIcon}
                            <span className="truncate">{isLive ? ev.title : ev.group_name}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
